import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { getIngestStatus, type IngestStatusResponse } from '../api/client';
import { VERSION_LABEL } from '../version';

/**
 * Post-Phase-11 — "About" modal triggered from the §Settings menu.
 *
 * Uses the native `<dialog>` element with `showModal()` so the
 * browser places the dialog in the top layer — rendered above all
 * other content and immune to CSS containing-block traps. (The
 * earlier `position: fixed` div approach was clipped by the topbar's
 * `backdrop-filter`, which silently establishes a containing block
 * for fixed descendants and shifted the modal off the top of the
 * viewport.)
 *
 * Top-layer rendering gives us:
 *   - Native viewport-centered placement
 *   - Built-in `::backdrop` dim
 *   - Escape-to-close handled by the platform
 *
 * We still wire a click handler that closes on backdrop click
 * (clicks where target === the dialog element itself, i.e. outside
 * the inner .card) and a `close` event handler that re-dispatches
 * to the parent so `<sat-settings>` can reset its `aboutOpen` state.
 *
 * Dismissal: backdrop click, ✕ button, or Escape key.
 *
 * Parent contract: `<sat-about-modal .open=${bool} @close=${handler}>`.
 */
@customElement('sat-about-modal')
export class SatAboutModal extends LitElement {
  /** Parent-controlled visibility. */
  @property({ type: Boolean }) open = false;

  /** Fetched ingest-status payload; `null` until first fetch resolves. */
  @state() private ingestStatus: IngestStatusResponse | null = null;
  /** Error from the most-recent fetch, surfaced inline. */
  @state() private fetchError: string | null = null;

  @query('dialog') private dialogEl!: HTMLDialogElement;

  override willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('open')) {
      if (this.open) this.setAttribute('data-open', '');
      else this.removeAttribute('data-open');
    }
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open')) {
      // Drive the dialog's modal state imperatively. showModal()
      // adds the dialog to the browser's top layer; close() removes
      // it. Both are idempotent against their own state but throw
      // when called on a dialog already in the opposite state, so
      // we guard.
      const dialog = this.dialogEl;
      if (dialog === null || dialog === undefined) return;
      if (this.open && !dialog.open) {
        dialog.showModal();
        if (this.ingestStatus === null && this.fetchError === null) {
          void this.refresh();
        }
      } else if (!this.open && dialog.open) {
        dialog.close();
      }
    }
  }

  private async refresh(): Promise<void> {
    try {
      this.ingestStatus = await getIngestStatus();
      this.fetchError = null;
    } catch (err) {
      this.fetchError = err instanceof Error ? err.message : String(err);
    }
  }

  private dispatchClose(): void {
    this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }

  /** Native `<dialog>` close event — fires for both ESC and our
   *  programmatic close(). Re-dispatch as our parent-facing `close`
   *  event so SettingsMenu can reset its state. */
  private handleDialogClose(): void {
    this.dispatchClose();
  }

  /** Close when the user clicks the dialog backdrop. The native
   *  `<dialog>` element receives clicks on its backdrop with
   *  `event.target === dialogElement`; clicks inside .card resolve
   *  to inner elements and don't match. */
  private handleDialogClick(e: MouseEvent): void {
    if (e.target === this.dialogEl) this.dispatchClose();
  }

  static styles = css`
    :host {
      /* The <dialog> in the top layer is what's visible; the host
         itself can stay display:contents since it's just a slot. */
      display: contents;
    }

    dialog {
      /* Use the browser's default modal centering. Reset padding +
         border so our .card-style box inside controls the chrome. */
      padding: 0;
      border: none;
      background: transparent;
      max-width: min(540px, calc(100vw - 2rem));
      max-height: calc(100vh - 2rem);
      color: var(--color-text);
    }
    dialog::backdrop {
      background: rgba(10, 14, 39, 0.72);
      backdrop-filter: blur(4px);
    }

    .card {
      background: var(--color-bg-elevated);
      border: 1px solid var(--color-border);
      border-radius: 6px;
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
      padding: 1.4rem 1.6rem;
      max-height: calc(100vh - 2rem);
      overflow-y: auto;
      font-family: var(--font-body);
    }
    header {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 1rem;
      margin-bottom: 1rem;
    }
    .title {
      font-family: var(--font-mono);
      font-size: 1.1rem;
      font-weight: 500;
      color: var(--color-text);
    }
    .title__glyph {
      color: var(--color-accent);
      margin-right: 0.3rem;
    }
    .close {
      background: transparent;
      border: 1px solid var(--color-border);
      border-radius: 3px;
      color: var(--color-text-muted);
      font-family: var(--font-mono);
      font-size: 0.9rem;
      padding: 0.2rem 0.55rem;
      cursor: pointer;
    }
    .close:hover { color: var(--color-accent); border-color: var(--color-accent); }
    .tagline {
      font-style: italic;
      color: var(--color-text-muted);
      margin: 0 0 1rem;
      font-size: 0.9rem;
    }
    .tagline em {
      color: var(--color-accent);
      font-style: italic;
    }
    h3 {
      margin: 1rem 0 0.4rem;
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--color-text-dim);
    }
    .version {
      font-family: var(--font-mono);
      font-size: 0.85rem;
      color: var(--color-text-muted);
    }
    .mission {
      font-size: 0.92rem;
      line-height: 1.5;
      color: var(--color-text);
      margin: 0;
    }
    .rows {
      display: grid;
      grid-template-columns: minmax(120px, max-content) 1fr;
      gap: 0.4rem 1rem;
      font-family: var(--font-mono);
      font-size: 0.82rem;
    }
    .rows__label {
      color: var(--color-text-muted);
    }
    .rows__value {
      color: var(--color-text);
    }
    .rows__value--null {
      color: var(--color-text-dim);
      font-style: italic;
    }
    .rows__count {
      color: var(--color-text-dim);
      font-size: 0.78rem;
      margin-left: 0.5rem;
    }
    .footer {
      margin-top: 1.2rem;
      padding-top: 0.8rem;
      border-top: 1px solid var(--color-border);
      font-size: 0.78rem;
      color: var(--color-text-dim);
    }
    .footer a {
      color: var(--color-accent);
      text-decoration: none;
    }
    .footer a:hover { text-decoration: underline; }
    .error {
      color: var(--color-warning);
      font-size: 0.82rem;
    }
  `;

  render() {
    // We render the <dialog> unconditionally; visibility is driven
    // by `dialog.showModal()` / `dialog.close()` in updated() so the
    // browser handles top-layer placement.
    return html`
      <dialog
        @close=${this.handleDialogClose}
        @click=${this.handleDialogClick}
        aria-labelledby="about-title"
      >
        ${this.open ? this.renderCard() : nothing}
      </dialog>
    `;
  }

  private renderCard() {
    return html`
      <div class="card">
        <header>
          <span class="title" id="about-title">
            <span class="title__glyph" aria-hidden="true">⊕</span>sat.trackr.live
          </span>
          <button
            class="close"
            type="button"
            aria-label="Close About"
            @click=${this.dispatchClose}
          >✕</button>
        </header>
        <p class="tagline">Space situational awareness, <em>legible</em>.</p>

        <h3>Version</h3>
        <div class="version">${VERSION_LABEL}</div>

        <h3>Mission</h3>
        <p class="mission">
          sat.trackr.live tracks everything humans have put in Earth orbit —
          satellites, launches, reentries, conjunctions, and the
          space-weather environment shaping it all — and presents it on a
          single time-scrubbable 3D globe with a full text-only fallback at
          <code>/text</code>. The project is open-source under AGPL-3.0-or-later
          and runs ad-free, account-free, and with telemetry strictly
          opt-in. Built so an amateur radio operator on a phone with three
          bars can find an ISS pass as easily as a researcher cross-checks
          a SOCRATES conjunction.
        </p>

        <h3>Data freshness</h3>
        ${this.renderIngestRows()}

        <div class="footer">
          License: <a href="https://www.gnu.org/licenses/agpl-3.0.html"
                     target="_blank" rel="noopener">AGPL-3.0-or-later</a>
          ·
          Source: <a href="https://github.com/CyberSecDef/sat.trackr.live"
                    target="_blank" rel="noopener">github.com/CyberSecDef/sat.trackr.live</a>
        </div>
      </div>
    `;
  }

  private renderIngestRows() {
    if (this.fetchError !== null) {
      return html`<div class="error">Couldn't load ingest status: ${this.fetchError}</div>`;
    }
    if (this.ingestStatus === null) {
      return html`<div class="rows__label">Loading…</div>`;
    }
    const s = this.ingestStatus;
    return html`
      <div class="rows">
        ${this.renderRow('TLE catalog',  s.tle_catalog)}
        ${this.renderRow('Launches',     s.launches)}
        ${this.renderRow('Reentries',    s.reentries)}
        ${this.renderRow('Conjunctions', s.conjunctions)}
      </div>
    `;
  }

  private renderRow(label: string, entry: { last_pulled_at: string | null; row_count: number }) {
    return html`
      <span class="rows__label">${label}</span>
      <span class="rows__value ${entry.last_pulled_at === null ? 'rows__value--null' : ''}">
        ${formatPulledAt(entry.last_pulled_at)}
        <span class="rows__count">· ${entry.row_count.toLocaleString()} rows</span>
      </span>
    `;
  }
}

/** Format an ISO-8601 timestamp into a human "5 min ago" string,
 *  falling back to YYYY-MM-DD HH:MM UTC when the delta is large.
 *  Null inputs render as "never" so the empty-table state is
 *  legible. */
function formatPulledAt(iso: string | null): string {
  if (iso === null) return 'never';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const deltaMs = Date.now() - t;
  if (deltaMs < 0)              return new Date(t).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
  if (deltaMs < 60_000)         return 'just now';
  if (deltaMs < 60 * 60_000)    return `${Math.round(deltaMs / 60_000)} min ago`;
  if (deltaMs < 24 * 3_600_000) return `${Math.round(deltaMs / 3_600_000)} h ago`;
  if (deltaMs < 7 * 86_400_000) return `${Math.round(deltaMs / 86_400_000)} d ago`;
  return new Date(t).toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-about-modal': SatAboutModal;
  }
}
